const MIC_STATE_DATA_TYPE = "mic-state-v1";

const remoteMicStates = new Map();
let syncRefCount = 0;
let subscribedConnection = null;
let removeListeners = null;

function getNafObject() {
  if (typeof NAF !== "undefined") {
    return NAF;
  }

  if (typeof window !== "undefined" && window.NAF) {
    return window.NAF;
  }

  if (typeof global !== "undefined" && global.NAF) {
    return global.NAF;
  }

  return null;
}

function getAppObject() {
  if (typeof window !== "undefined") {
    return window.APP;
  }

  if (typeof global !== "undefined") {
    return global.APP;
  }

  return undefined;
}

function getSceneEl() {
  if (typeof AFRAME === "undefined") {
    return null;
  }

  return AFRAME?.scenes?.[0] || null;
}

function emitMicPresenceUpdated(detail) {
  const sceneEl = getSceneEl();

  if (!sceneEl) {
    return;
  }

  if (typeof sceneEl.emit === "function") {
    sceneEl.emit("mic_presence_updated", detail);
    return;
  }

  if (typeof sceneEl.dispatchEvent === "function" && typeof CustomEvent !== "undefined") {
    sceneEl.dispatchEvent(new CustomEvent("mic_presence_updated", { detail }));
  }
}

function getLocalMutedState() {
  const isMicEnabled = getAppObject()?.dialog?.isMicEnabled;

  if (typeof isMicEnabled !== "boolean") {
    return null;
  }

  return !isMicEnabled;
}

function setRemoteMicState(sessionId, muted) {
  if (!sessionId || typeof muted !== "boolean") {
    return;
  }

  const previousState = remoteMicStates.get(sessionId);

  if (previousState?.muted === muted) {
    return;
  }

  remoteMicStates.set(sessionId, { muted });
  emitMicPresenceUpdated({ sessionId, muted });
}

function removeRemoteMicState(sessionId) {
  if (!sessionId) {
    return;
  }

  if (remoteMicStates.delete(sessionId)) {
    emitMicPresenceUpdated({ sessionId });
  }
}

function onReceiveMicState(fromClientId, _dataType, data) {
  setRemoteMicState(fromClientId, data?.muted);
}

function registerConnectionSubscription() {
  const connection = getNafObject()?.connection;

  if (!connection?.subscribeToDataChannel) {
    return;
  }

  if (subscribedConnection === connection) {
    return;
  }

  subscribedConnection?.unsubscribeToDataChannel?.(MIC_STATE_DATA_TYPE);
  connection.subscribeToDataChannel(MIC_STATE_DATA_TYPE, onReceiveMicState);
  subscribedConnection = connection;
}

function isNafConnectionReady(connection) {
  if (!connection) {
    return false;
  }

  if (typeof connection.isConnected === "function") {
    return connection.isConnected();
  }

  return Boolean(getNafObject()?.clientId);
}

function broadcastLocalMicState(targetClientId) {
  const connection = getNafObject()?.connection;
  const muted = getLocalMutedState();

  if (!isNafConnectionReady(connection) || typeof muted !== "boolean") {
    return;
  }

  if (targetClientId) {
    connection.sendDataGuaranteed?.(targetClientId, MIC_STATE_DATA_TYPE, { muted });
  } else {
    connection.broadcastDataGuaranteed?.(MIC_STATE_DATA_TYPE, { muted });
  }
}

function addGlobalListeners() {
  if (removeListeners) {
    return;
  }

  const onConnected = () => {
    registerConnectionSubscription();
    broadcastLocalMicState();
  };
  const onClientConnected = event => {
    registerConnectionSubscription();
    broadcastLocalMicState(event.detail?.clientId);
  };
  const onClientDisconnected = event => {
    removeRemoteMicState(event.detail?.clientId);
  };
  const onLocalMicStateChanged = ({ enabled }) => {
    const naf = getNafObject();

    if (typeof enabled === "boolean" && naf?.clientId) {
      setRemoteMicState(naf.clientId, !enabled);
    }

    broadcastLocalMicState();
  };

  document.body?.addEventListener?.("connected", onConnected);
  document.body?.addEventListener?.("clientConnected", onClientConnected);
  document.body?.addEventListener?.("clientDisconnected", onClientDisconnected);
  getAppObject()?.dialog?.on?.("mic-state-changed", onLocalMicStateChanged);

  removeListeners = () => {
    document.body?.removeEventListener?.("connected", onConnected);
    document.body?.removeEventListener?.("clientConnected", onClientConnected);
    document.body?.removeEventListener?.("clientDisconnected", onClientDisconnected);
    getAppObject()?.dialog?.off?.("mic-state-changed", onLocalMicStateChanged);
    removeListeners = null;
  };
}

export function ensureNetworkedMicStateSync() {
  syncRefCount += 1;

  registerConnectionSubscription();
  addGlobalListeners();

  return () => {
    syncRefCount = Math.max(0, syncRefCount - 1);

    if (syncRefCount === 0) {
      removeListeners?.();
      subscribedConnection?.unsubscribeToDataChannel?.(MIC_STATE_DATA_TYPE);
      subscribedConnection = null;
    }
  };
}

export function getNetworkedMicState(sessionId) {
  return remoteMicStates.get(sessionId);
}

export function resetNetworkedMicStateSyncForTests() {
  syncRefCount = 0;
  removeListeners?.();
  subscribedConnection?.unsubscribeToDataChannel?.(MIC_STATE_DATA_TYPE);
  subscribedConnection = null;
  remoteMicStates.clear();
}
